import { defineComponent, ref, onBeforeUnmount, onMounted, watch } from 'vue'
import type { PropType, ExtractPropTypes } from 'vue'
import OrionError from '../../error/OrionError'

import { createEditor, createToolbar } from '@wangeditor/editor'
import type { IDomEditor, IEditorConfig, IToolbarConfig } from '@wangeditor/editor'

export const richTextEditorProps = () => ({
  modelValue: String,
  readonly: {
    type: Boolean,
    default: false,
  },
  uploadImage: Function as PropType<(file: File) => { url: string; alt?: string; href?: string } | null | false>,
  placeholder: {
    type: String,
    default: '请输入内容...',
  },
})

export type RichTextEditorProps = Partial<ExtractPropTypes<ReturnType<typeof richTextEditorProps>>>

type InsertFnType = (url: string, alt: string, href: string) => void

export default defineComponent({
  name: 'ORichTextEditor',
  inheritAttrs: false,
  props: richTextEditorProps(),
  emits: ['update:modelValue'],
  setup(props, { slots, attrs, emit, expose }) {
    const { placeholder } = props

    const editorRef = ref<IDomEditor | null>(null) // TS 语法
    const editorWrapperDOMRef = ref<HTMLDivElement>()
    const editorContainerDOMRef = ref<HTMLDivElement>()
    const toolbarContainerDOMRef = ref<HTMLDivElement>()

    expose({
      focus: () => {
        editorRef.value?.focus()
      },
    })

    onMounted(() => {
      // 编辑器配置
      const editorConfig: IEditorConfig = {
        placeholder,
        onChange(editor) {
          const html = editor.getHtml()
          emit('update:modelValue', html)
        },
        readOnly: props.readonly,
        autoFocus: true,
        scroll: true,
        MENU_CONF: {
          uploadImage: {
            // 自定义上传
            async customUpload(file: File, insertFn: InsertFnType) {
              if (props.uploadImage) {
                const ret = await props.uploadImage(file)

                if (ret == null || ret === false) {
                  return
                }

                if (!ret.url) {
                  throw new OrionError(`RichTextEditor: 图片上传接口 uploadImage 的返回数据中未包含 url 字段。`)
                }

                insertFn(ret.url, ret.alt, ret.href)
              } else {
                throw new OrionError(`RichTextEditor: 未实现文件上传接口onUpload。`)
              }
            },
          },
        },
      }
      const editor = createEditor({
        selector: editorContainerDOMRef.value!,
        html: props.modelValue, // 初始值
        config: editorConfig,
        mode: 'default',
      })
      editorRef.value = editor

      // 工具栏配置
      const toolbarConfig: Partial<IToolbarConfig> = {
        excludeKeys: ['blockquote', 'bulletedList', 'numberedList', 'todo', 'emotion', 'insertLink', 'insertImage', 'group-video', 'insertTable', 'codeBlock'],
      }

      const toolbar = createToolbar({
        editor,
        selector: toolbarContainerDOMRef.value!,
        config: toolbarConfig,
        mode: 'default', // or 'simple'
      })

      // const t = DomEditor.getToolbar(editor)
      // const curToolbarConfig = t.getConfig()
      // console.log(curToolbarConfig.toolbarKeys) // 当前菜单排序和分组
    })

    watch(
      () => props.modelValue,
      (modelValue) => {
        if (editorRef.value) {
          editorRef.value.setHtml(modelValue || '')
        }
      }
    )

    watch(
      () => props.readonly,
      (readonly) => {
        if (editorRef.value) {
          if (readonly) {
            editorRef.value.disable()
          } else {
            editorRef.value.enable()
          }
        }
      }
    )

    onBeforeUnmount(() => {
      if (editorRef.value == null) return
      editorRef.value.destroy()
      editorRef.value = null
    })

    return () => {
      return (
        <div class="orion-rich-text-editor—wrapper" ref={editorWrapperDOMRef} style={{ border: '1px solid #ccc', zIndex: 100 }}>
          <div class="orion-rich-text-toolbar-container" ref={toolbarContainerDOMRef} style={{ borderBottom: '1px solid #ccc' }}></div>
          <div class="orion-rich-text-editor-container" ref={editorContainerDOMRef} style={{ overflowY: 'hidden' }}></div>
        </div>
      )
    }
  },
})
